import { useEffect } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel
} from '@headlessui/react';
import { KALTURA_PARTNER_ID } from 'constants/videos';
import { RELIABILITY_TEST_UICONF } from 'components/ReliabilityTest/utils';
import useKalturaScript from 'hooks/useKalturaScript';

interface RewatchVideoAccordionProps {
  videoHostId: string;
}

function getKalturaConfiguration(targetId: string) {
  return {
    targetId,
    provider: {
      partnerId: KALTURA_PARTNER_ID,
      uiConfId: RELIABILITY_TEST_UICONF
    },
    playback: {
      autoplay: false
    }
  };
}

function RewatchVideoAccordion({ videoHostId }: RewatchVideoAccordionProps) {
  const playerId = `rewatch_video_player_${videoHostId}`;
  const isPlayerLoaded = useKalturaScript();

  useEffect(() => {
    const kalturaPlayer = (window as any).KalturaPlayer?.getPlayer(playerId);

    return () => {
      kalturaPlayer?.destroy();
    };
  }, [playerId]);

  function handleOpen(open: boolean) {
    if (open && isPlayerLoaded) {
      setTimeout(() => {
        const existingPlayer = (window as any).KalturaPlayer?.getPlayer(
          playerId
        );
        if (!existingPlayer) {
          const kalturaPlayer = (window as any).KalturaPlayer?.setup(
            getKalturaConfiguration(playerId)
          );
          kalturaPlayer?.loadMedia({ entryId: videoHostId });
        }
      }, 100);
    }
  }

  return (
    <div className="print:hidden bg-[#F9F9F9] px-1 sm:px-8 lg:px-20 lg:mx-12 pb-4">
      <Disclosure>
        {({ open }) => {
          handleOpen(open);
          return (
            <div className="bg-[#D7DFE8] rounded-lg">
              <DisclosureButton className="flex w-full items-center justify-between px-6 py-4 text-left focus-visible:outline-none focus-visible:ring-ds-border-focus focus-visible:ring-2 focus-visible:ring-offset-2 transition-all">
                <div className="flex items-center gap-3">
                  <svg
                    width="46"
                    height="46"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22.247 11.4405C21.7755 11.1695 21.214 11.1715 20.744 11.4455L18 13.0465V18.454L20.744 20.055C20.9805 20.193 21.24 20.262 21.5 20.262C21.7565 20.262 22.013 20.195 22.247 20.0605C22.7185 19.7895 23 19.3035 23 18.76V12.742C23 12.1985 22.7185 11.712 22.247 11.4415V11.4405Z"
                      fill="#385E8E"
                    />
                    <path
                      d="M15.301 10.6435C16.331 9.868 17 8.6385 17 7.25C17 4.903 15.097 3 12.75 3C10.403 3 8.5 4.903 8.5 7.25C8.5 8.5545 9.089 9.7205 10.014 10.5H7.2935C8.0335 9.815 8.5 8.838 8.5 7.75C8.5 5.679 6.821 4 4.75 4C2.679 4 1 5.679 1 7.75C1 8.964 1.5795 10.0405 2.4745 10.726C1.6075 11.119 1 11.988 1 13V18.5C1 19.8785 2.1215 21 3.5 21H14.5C15.8785 21 17 19.8785 17 18.5V13C17 11.903 16.2855 10.9795 15.301 10.6435ZM3.25 7.75C3.25 6.9215 3.9215 6.25 4.75 6.25C5.5785 6.25 6.25 6.9215 6.25 7.75C6.25 8.5785 5.5785 9.25 4.75 9.25C3.9215 9.25 3.25 8.5785 3.25 7.75ZM6 18.5H4C3.724 18.5 3.5 18.276 3.5 18C3.5 17.724 3.724 17.5 4 17.5H6C6.276 17.5 6.5 17.724 6.5 18C6.5 18.276 6.276 18.5 6 18.5ZM12.75 9.25C11.6455 9.25 10.75 8.3545 10.75 7.25C10.75 6.1455 11.6455 5.25 12.75 5.25C13.8545 5.25 14.75 6.1455 14.75 7.25C14.75 8.3545 13.8545 9.25 12.75 9.25Z"
                      fill="#385E8E"
                    />
                  </svg>
                  <span className="text-base font-bold leading-none text-gray-900 pl-2">
                    {i18n.ft(messages.header)}
                  </span>
                </div>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#385E8E] text-white">
                  {open ? (
                    <svg
                      width="12"
                      height="2"
                      viewBox="0 0 12 2"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1H11"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 1V11M1 6H11"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
              </DisclosureButton>

              <DisclosurePanel
                static
                className={`px-4 pb-4 ${open ? '' : 'hidden'}`}
              >
                <div
                  id={playerId}
                  className="w-full aspect-video bg-black rounded"
                />
              </DisclosurePanel>
            </div>
          );
        }}
      </Disclosure>
    </div>
  );
}

export default RewatchVideoAccordion;
